import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { patronDeRuta } from "../utils/pantallasApp";

// Registro de uso de la app (solo frontend). Por cada visita a una pantalla
// guarda cuánto tiempo ACTIVO se ha estado en ella:
//   - solo cuenta con la pestaña/app VISIBLE (móvil bloqueado, app en segundo
//     plano u otra pestaña delante → no cuenta), y
//   - el tiempo desde el último toque (ratón, teclado, scroll) queda "en espera":
//     se suma si vuelve a haber actividad o si el usuario sale él mismo (cambia
//     de pantalla, bloquea el móvil, cierra) antes de 2 minutos; si se llega a
//     2 minutos sin tocar la app, ese tiempo NO se cuenta (pestaña olvidada).
// Envíos: al cambiar de pantalla, al ocultar la app y al cerrarla; y como red
// de seguridad (en el móvil el aviso de cierre no siempre llega) cada 2 min si
// hay tiempo pendiente. Cada trozo lleva el mismo visita_id y el servidor lo
// SUMA a la fila de esa visita: una fila por visita, se envíe las veces que se
// envíe. El usuario y el rol los pone el servidor a partir del token.

const TICK_MS = 5000;
const INACTIVIDAD_MS = 2 * 60 * 1000;
const ENVIO_MS = 2 * 60 * 1000;
const SESION_KEY = "uso_sesion";
const SESION_CADUCA_MS = 30 * 60 * 1000;

const esMovil = () =>
  (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) ||
  /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent);
const esPwa = () =>
  (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true;

// id de sesión: se renueva tras 30 min sin actividad
function idSesion() {
  try {
    const raw = sessionStorage.getItem(SESION_KEY);
    const s = raw ? JSON.parse(raw) : null;
    if (s && Date.now() - s.ultimo < SESION_CADUCA_MS) {
      s.ultimo = Date.now();
      sessionStorage.setItem(SESION_KEY, JSON.stringify(s));
      return s.id;
    }
    const nuevo = { id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2)), ultimo: Date.now() };
    sessionStorage.setItem(SESION_KEY, JSON.stringify(nuevo));
    return nuevo.id;
  } catch (_) {
    return null;
  }
}

function tiempoCarga() {
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    if (nav && nav.domContentLoadedEventEnd > 0) return Math.round(nav.domContentLoadedEventEnd);
  } catch (_) {}
  return null;
}

export default function UsoTracker() {
  const location = useLocation();
  const actual = useRef(null);        // visita en curso
  const cola = useRef([]);            // visitas terminadas pendientes de enviar
  const ultimaInteraccion = useRef(Date.now());
  const pendiente = useRef(0);        // segundos desde el último toque, aún sin contar
  const cargaEnviada = useRef(false);

  // pasa el tiempo en espera a la visita (solo mientras no han pasado 2 min sin tocar)
  const consolidar = () => {
    if (actual.current && pendiente.current > 0) actual.current.segundos += pendiente.current;
    pendiente.current = 0;
  };

  const cerrarVisita = () => {
    consolidar();
    const v = actual.current;
    actual.current = null;
    if (v && v.segundos > 0) cola.current.push(v);
  };

  const enviar = () => {
    const token = localStorage.getItem("access_token");
    if (!token || cola.current.length === 0) return;
    const eventos = cola.current.splice(0, 60);
    try {
      // keepalive: la petición se completa aunque se esté cerrando la pestaña
      // (sendBeacon no permite mandar la cabecera Authorization).
      fetch("/api/uso/eventos", {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ eventos }),
      }).catch(() => {});
    } catch (_) {}
  };

  // Cambio de pantalla: cerrar la anterior (y enviarla) y abrir la nueva
  useEffect(() => {
    cerrarVisita();
    enviar();
    const patron = patronDeRuta(location.pathname);
    if (patron && localStorage.getItem("access_token")) {
      actual.current = {
        pantalla: patron,
        inicio_ms: Date.now(),
        segundos: 0,
        dispositivo: esMovil() ? "movil" : "pc",
        pwa: esPwa(),
        sesion_id: idSesion(),
        visita_id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2)),
        carga_ms: cargaEnviada.current ? null : tiempoCarga(),
      };
      cargaEnviada.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Reloj de tiempo activo, detección de interacción y envíos
  useEffect(() => {
    const marcar = () => { consolidar(); ultimaInteraccion.current = Date.now(); };
    const eventos = ["pointerdown", "keydown", "scroll", "touchstart", "wheel"];
    eventos.forEach((e) => window.addEventListener(e, marcar, { passive: true, capture: true }));
    let ultimoMov = 0;
    const mov = () => { const t = Date.now(); if (t - ultimoMov > 5000) { ultimoMov = t; marcar(); } };
    window.addEventListener("mousemove", mov, { passive: true });

    const tick = setInterval(() => {
      const v = actual.current;
      if (!v) return;
      if (document.visibilityState !== "visible") return;
      if (Date.now() - ultimaInteraccion.current < INACTIVIDAD_MS) pendiente.current += TICK_MS / 1000;
      else pendiente.current = 0; // 2 min sin tocar: ese tiempo no cuenta
    }, TICK_MS);

    // red de seguridad cada 2 min: envía lo acumulado de la visita en curso
    // (mismo visita_id, el servidor lo suma) por si el móvil mata la app
    const envio = setInterval(() => {
      const v = actual.current;
      if (v && v.segundos > 0) {
        cola.current.push({ ...v });
        actual.current = { ...v, inicio_ms: Date.now(), segundos: 0, carga_ms: null };
      }
      enviar();
    }, ENVIO_MS);

    const alOcultar = () => {
      if (document.visibilityState === "hidden") {
        consolidar(); // ocultarla antes de 2 min sin tocar: ese rato sí cuenta
        const v = actual.current;
        if (v && v.segundos > 0) {
          cola.current.push({ ...v });
          actual.current = { ...v, inicio_ms: Date.now(), segundos: 0, carga_ms: null };
        }
        enviar();
      } else {
        ultimaInteraccion.current = Date.now(); // volver a la app cuenta como actividad
      }
    };
    const alSalir = () => { cerrarVisita(); enviar(); };
    document.addEventListener("visibilitychange", alOcultar);
    window.addEventListener("pagehide", alSalir);

    return () => {
      clearInterval(tick); clearInterval(envio);
      eventos.forEach((e) => window.removeEventListener(e, marcar, { capture: true }));
      window.removeEventListener("mousemove", mov);
      document.removeEventListener("visibilitychange", alOcultar);
      window.removeEventListener("pagehide", alSalir);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
